import { arrayUnion, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { deleteToken, getToken, onMessage } from "firebase/messaging";
import { toast } from "react-toastify";
import { db, messaging } from "../firebase";

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;
const SERVICE_WORKER_PATH = "/firebase-messaging-sw.js";

const getServiceWorkerRegistration = async () => {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers are not supported in this browser.");
  }

  const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
  await navigator.serviceWorker.ready;
  return registration;
};

export const registerNotificationServiceWorker = async () => {
  if (!isNotificationSupported()) {
    return null;
  }

  try {
    return await getServiceWorkerRegistration();
  } catch (error) {
    console.warn("Unable to register notification service worker:", error);
    return null;
  }
};

const saveFCMToken = async (userId, token) => {
  await setDoc(
    doc(db, "fcmTokens", userId),
    {
      token,
      tokens: arrayUnion(token),
      userId,
      updatedAt: serverTimestamp(),
      browser: navigator.userAgent,
    },
    { merge: true }
  );
};

export const refreshNotificationToken = async (userId) => {
  if (!userId || !messaging || Notification.permission !== "granted") {
    return null;
  }

  if (!VAPID_KEY || VAPID_KEY === "YOUR_VAPID_KEY_HERE") {
    throw new Error("VITE_FIREBASE_VAPID_KEY is not configured.");
  }

  const serviceWorkerRegistration = await getServiceWorkerRegistration();
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration,
  });

  if (!token) {
    throw new Error("Firebase did not return a notification token.");
  }

  await saveFCMToken(userId, token);
  return token;
};

// This must be called from a user gesture so browsers can display the prompt.
export const requestNotificationPermission = async (userId) => {
  if (!isNotificationSupported()) {
    throw new Error("Browser notifications are not supported on this device.");
  }

  const permission =
    Notification.permission === "default"
      ? await Notification.requestPermission()
      : Notification.permission;

  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifications are blocked. Allow them in your browser site settings."
        : "Notification permission was not granted."
    );
  }

  return refreshNotificationToken(userId);
};

export const disableNotifications = async () => {
  if (!messaging) return;

  try {
    await deleteToken(messaging);
  } catch (error) {
    console.warn("Unable to delete the local FCM token:", error);
  }
};

export const getUserFCMToken = async (userId) => {
  const tokenDoc = await getDoc(doc(db, "fcmTokens", userId));
  return tokenDoc.exists() ? tokenDoc.data().token || null : null;
};

export const showBrowserNotification = async ({ title, body, tag, data = {} }) => {
  if (Notification.permission !== "granted" || !("serviceWorker" in navigator)) {
    return false;
  }

  const registration = await navigator.serviceWorker.ready;
  await registration.showNotification(title || "New Activity", {
    body: body || "You have a new notification",
    icon: "/assets/images/bhit-logo.png",
    badge: "/assets/images/bhit-logo.png",
    tag: tag || "notification",
    data,
  });
  return true;
};

export const setupForegroundNotifications = () => {
  if (!messaging) return () => {};

  return onMessage(messaging, (payload) => {
    const title = payload.notification?.title || payload.data?.title || "New Activity";
    const body = payload.notification?.body || payload.data?.body || "You have a new notification";

    toast.info(`${title}: ${body}`);
  });
};

export const isNotificationSupported = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  "serviceWorker" in navigator &&
  messaging !== null;

export const getNotificationPermissionStatus = () => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "not-supported";
  }
  return Notification.permission;
};
